/**
 * 유사 문제 파이프라인 — 서버 문항 → 폼 문항 · 응답 적용(원본 아래 삽입). 지문은 questionCardAiRevision.
 */
import { nanoid } from 'nanoid';

import { type QuizGenerateItem } from '@/api/ai/type';

import { MAX_QUIZ_COUNT } from '../../../constant';
import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { adaptAiGeneratedQuizzesToBuildForm } from '../../ai-builder-chat/utils/adaptAiGeneratedQuizzesToBuildForm';
import {
  type ActionTarget,
  type ApplyOutcome,
  type FormWriter,
} from '../types/questionCardAi.types';

import {
  type SimilarQuestionResponseDto,
  type WireQuestionDto,
} from './questionCardAiEndpoints';

/** ox 는 'o'|'x' 스칼라, 그 외는 배열로 온다 — 어댑터는 항상 배열을 본다. */
const toAnswerList = (answer: WireQuestionDto['answer']): string[] =>
  typeof answer === 'string' ? [answer] : answer ?? [];

/** 서버 결과 문항 → 폼 문항. 채팅 결과와 같은 어댑터를 거쳐 rich text·LaTeX·성취기준 지문을 동일하게 받는다. */
export const toBuildQuizItem = (
  question: WireQuestionDto,
): BuildQuizListItem => {
  const generateItem = {
    type: question.quiz_type,
    question: question.question,
    options: question.options ?? [],
    answer: toAnswerList(question.answer),
    comment: question.comment ?? '',
    standard: [],
    difficulty: question.difficulty ?? undefined,
    curriculumList: (question.curriculum_list ?? []).map(item => ({
      standardCode: item.standard_code,
      subject: item.subject,
    })),
  } as QuizGenerateItem;

  const [item] = adaptAiGeneratedQuizzesToBuildForm([generateItem]);
  if (item == null) throw new Error('Failed to adapt AI question');

  return { ...item, id: nanoid(), origin: { type: 'similar' } };
};

/** 유사 문제 결과를 원본 바로 아래에 삽입한다. 상한이면 alert 를 띄우고 폐기한다. */
export const applySimilarQuestionResponse = (
  { question }: Pick<SimilarQuestionResponseDto, 'question'>,
  { index, count }: ActionTarget,
  writer: FormWriter,
): ApplyOutcome => {
  if (count >= MAX_QUIZ_COUNT) {
    writer.openQuizLimitAlert();
    return { discarded: 'quiz_limit', appliedCount: 0 };
  }
  const item = toBuildQuizItem(question);
  writer.insertAfter(index, [item]);
  writer.highlight([item.id]);
  return { discarded: null, appliedCount: 1 };
};
